<template>
  <section>
    <div class="max-w-screen-xl mx-auto px-4 mb-20">
      <!-- Header Section -->
      <div class="max-w-max mx-auto relative mb-8">
        <NuxtImg src="/images/circuit.png" width="298" height="180" alt="Shopping Bag"
          class="w-40 sm:w-56 md:w-72 lg:w-[298px] h-auto" loading="lazy" />
        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                 flex items-center justify-center 
                 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 
                 rounded-2xl bg-[image:var(--primary-gradient)]">
          <NsPriceSectionIcon class="w-6 sm:w-8 md:w-10 h-auto" />
        </div>
      </div>

      <!-- Heading -->
      <h2 class="text-xl sm:text-2xl md:text-3xl text-[var(--text-secondary)] 
               text-center font-bold -mt-4 sm:-mt-6 md:-mt-7 mb-4 px-2">
        Choose Your Plan
      </h2>
      <p class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)] 
               max-w-4xl text-center mx-auto mb-10 px-2">
        Our premium frontend solution offers flexible pricing tailored to your needs.
        Enjoy exclusive features and seamless integration with our backend.
      </p>

      <!-- Pricing Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
        <Card unstyled v-for="(plan, index) in plans" :key="index"
          class="rounded-2xl shadow-md hover:shadow-lg transition p-4 sm:p-6">
          <template #content>
            <div>
              <!-- Illustration -->
              <div class="mb-6 flex justify-center">
                <NuxtImg :src="plan.image" :alt="plan.title" width="430" height="320"
                  class="w-full h-auto max-h-64 object-contain" loading="lazy" />
              </div>

              <!-- Title & Description -->
              <h3 class="text-lg sm:text-xl md:text-2xl font-bold 
                       text-[var(--text-secondary)] mb-2">
                {{ plan.title }}
              </h3>
              <p class="text-[var(--text-muted)] text-sm sm:text-base mb-6 min-h-12">
                {{ plan.description }}
              </p>

              <!-- Features -->
              <div class="space-y-4">
                <div v-for="(feature, fIndex) in plan.features" :key="fIndex" class="flex items-start gap-3">
                  <div class="w-5 h-5 flex items-center justify-center 
                           rounded-full border-2 shrink-0">
                    <i class="pi pi-check text-white text-[10px]"></i>
                  </div>
                  <div class="text-sm sm:text-base leading-snug">
                    <span class="text-blue-400 font-semibold">{{ feature.title }}</span>
                    <span class="text-[var(--text-muted)]"> {{ feature.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- CTA Section -->
     <div
                class="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center w-full max-w-[280px] sm:max-w-none mx-auto">
                <!-- Gradient Button -->
                <Button unstyled label="Buy Now" iconPos="left" class="group !rounded-xl !px-4 sm:!px-5 
         bg-[image:var(--primary-gradient)] hover:bg-[image:var(--primary-gradient-reverse)]
         transition-all duration-1000 ease-in-out
         !py-2.5 sm:!py-3 font-semibold
         text-[var(--text-secondary)] 
         flex items-center justify-center gap-2 sm:gap-3 
         w-full sm:w-auto text-sm sm:text-base overflow-hidden">

                    <span class="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 
             rounded-full bg-white 
             transform transition-all duration-1000 ease-in-out 
             group-hover:translate-x-20">
                        <i class="pi pi-angle-right text-green-500 font-bold text-sm sm:text-lg"></i>
                    </span>
                    <span class="transition-all duration-1000 ease-in-out group-hover:-translate-x-1/2">
                        Buy Now
                    </span>
                </Button>


                <!-- Secondary Button -->
                <Button unstyled label="Contact us for Enterprise Plans" icon="pi pi-calendar" :pt="{ icon: { class: '' } }"
                    class="!rounded-xl !px-4 sm:!px-5 bg-[var(--background-btn)] !py-2.5 sm:!py-3 font-semibold text-[var(--text-secondary)] flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto text-sm sm:text-base">
                </Button>
            </div>
    </div>
  </section>
</template>

<script setup lang="ts">

const NsPriceSectionIcon = defineAsyncComponent(() =>
  import("~/components/atoms/icons/NsPriceSectionIcon.vue")
);

const plans = [
  {
    title: "Single Site License",
    image: "/images/single-site-plan.png",
    description: "Ideal for small to medium businesses",
    features: [
      { title: "Affordable Access:", text: " Cost-effective plan for growing businesses." },
      { title: "Quick Setup:", text: " Easy integration with your backend." },
      { title: "Core Features:", text: " Includes essential e-commerce tools." },
      { title: "Support Included:", text: " Assistance as your business scales." }
    ],
  },
  {
    title: "Multi-Site License",
    image: "/images/multi-site-plan.png",
    description: "Perfect for expanding brands",
    features: [
      { title: "Manage Multiple Sites:", text: " Run multiple storefronts with ease." },
      { title: "Unified Control:", text: " Centralized dashboard for all sites." },
      { title: "Performance Tracking:", text: " Analytics across all sites." },
      { title: "Scalable:", text: " Grows with your business needs." }
    ],
  },
  {
    title: "Enterprise",
    image: "/images/enterprise-plan.png",
    description: "Fully customizable plan for large-scale businesses",
    features: [
      { title: "Advanced Customization:", text: " Tailored to your business model." },
      { title: "Priority Support:", text: " Dedicated account manager." },
      { title: "Enhanced Security:", text: " Protects sensitive data." },
      { title: "Comprehensive Integration:", text: " Works with existing systems." },
      { title: "Custom Analytics:", text: " Deep insights for decision-making." }
    ],
  }
];
</script>
